import { useCallback, useRef } from 'react';

type RouteModule = () => Promise<{ default: React.ComponentType }>;

const routeModules: Record<string, RouteModule> = {
  '/': () => import('@/pages/Index'),
  '/discussions': () => import('@/pages/Discussions'),
  '/bible-study': () => import('@/pages/BibleStudy'),
  '/prayer': () => import('@/pages/Prayer'),
  '/accountability': () => import('@/pages/Accountability'),
  '/user-browse': () => import('@/pages/UserBrowse'),
  '/friends': () => import('@/pages/Friends'),
  '/profile': () => import('@/pages/Profile'),
  '/admin': () => import('@/pages/Admin'),
  '/donate': () => import('@/pages/Donate'),
  '/bible-explorer': () => import('@/pages/BibleExplorer'),
};

const prefetchedRoutes = new Set<string>();

export function useRoutePrefetch() {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const prefetchRoute = useCallback((path: string) => {
    if (prefetchedRoutes.has(path)) return;
    
    const routeModule = routeModules[path];
    if (routeModule) {
      prefetchedRoutes.add(path);
      routeModule().catch(() => {
        prefetchedRoutes.delete(path);
      });
    }
  }, []);

  const prefetchOnHover = useCallback((path: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      prefetchRoute(path);
    }, 100);
  }, [prefetchRoute]);

  const cancelPrefetch = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return { prefetchRoute, prefetchOnHover, cancelPrefetch };
}

export function prefetchCriticalRoutes() {
  const criticalRoutes = ['/discussions', '/prayer', '/bible-study'];
  
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(() => {
      criticalRoutes.forEach(route => {
        const routeModule = routeModules[route];
        if (routeModule && !prefetchedRoutes.has(route)) {
          prefetchedRoutes.add(route);
          routeModule().catch(() => {
            prefetchedRoutes.delete(route);
          });
        }
      });
    });
  }
}
